import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft,
    ArrowRight,
    CheckCircle2,
    Sparkles,
    Percent,
    Wallet,
    KeyRound,
    Link2Off,
    Palmtree,
    Gem,
    Layers,
    Landmark,
    HandCoins,
} from "lucide-react";
import { MORTGAGE_PAGES } from "@/data/mortgageContent";
import LeadFormCard from "@/components/LeadFormCard";

const ICONS = {
    Percent,
    Wallet,
    KeyRound,
    Link2Off,
    Palmtree,
    Gem,
    Layers,
    Landmark,
    Sparkles,
    HandCoins,
};

function CheckList({ items, testidPrefix }) {
    return (
        <ul className="mt-5 space-y-3.5">
            {items.map((item, i) => (
                <li
                    key={i}
                    data-testid={`${testidPrefix}-${i}`}
                    className="flex items-start gap-3.5"
                >
                    <CheckCircle2
                        size={20}
                        className="text-emerald-500 shrink-0 mt-0.5"
                    />
                    <span className="text-slate-700 leading-relaxed">
                        {item}
                    </span>
                </li>
            ))}
        </ul>
    );
}

export default function MortgagePage({ slug: slugProp }) {
    const params = useParams();
    const slug = slugProp || params.slug;
    const navigate = useNavigate();
    const page = MORTGAGE_PAGES[slug];

    useEffect(() => {
        if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
        window.scrollTo(0, 0);
    }, [slug]);

    const goToForm = () => {
        navigate("/", { state: { scrollTo: "contacto" } });
    };

    if (!page) {
        return (
            <div className="min-h-screen bg-[#112338] flex flex-col items-center justify-center px-6 text-center">
                <h1 className="font-display text-3xl font-bold text-white">
                    Producto no encontrado
                </h1>
                <Link
                    data-testid="not-found-back"
                    to="/"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-500 text-[#0B1A2B] font-bold px-6 py-3"
                >
                    <ArrowLeft size={17} /> Volver al inicio
                </Link>
            </div>
        );
    }

    const Icon = ICONS[page.icon] || Sparkles;

    return (
        <div data-testid={`mortgage-page-${slug}`} className="min-h-screen bg-white">
            <header className="absolute top-0 inset-x-0 z-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
                    <Link to="/" data-testid="detail-logo" aria-label="Volver al inicio">
                        <img
                            src="/assets/logo-white-trim.png"
                            alt="Keypoteca"
                            className="h-8 sm:h-9 w-auto"
                        />
                    </Link>
                    <button
                        data-testid="detail-cta"
                        onClick={goToForm}
                        className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0B1A2B] text-sm font-bold px-5 py-2.5 transition-colors duration-200"
                    >
                        Solicitar estudio
                    </button>
                </div>
            </header>

            <section className="relative overflow-hidden bg-[#112338] noise-overlay pt-36 pb-20 lg:pt-44 lg:pb-24 px-4 sm:px-6 lg:px-8">
                <div className="absolute inset-0 blueprint-grid" />
                <div
                    className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full pointer-events-none"
                    style={{
                        background:
                            "radial-gradient(circle at center, rgba(16,185,129,0.13) 0%, transparent 70%)",
                    }}
                />
                <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <Link
                            data-testid="detail-back"
                            to="/"
                            className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
                        >
                            <ArrowLeft size={16} /> Todas las hipotecas
                        </Link>
                        <div className="mt-8 flex items-center gap-5">
                            <span className="flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/15 border border-emerald-400/30 text-emerald-400 shrink-0">
                                <Icon size={30} />
                            </span>
                        </div>
                        <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white text-balance">
                            {page.title}
                        </h1>
                        <p className="mt-5 text-lg sm:text-xl text-emerald-300/90 font-medium">
                            {page.subtitle}
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 32 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                    >
                        <LeadFormCard
                            mortgageType={page.title}
                            title={`Me interesa: ${page.title}`}
                        />
                    </motion.div>
                </div>
            </section>

            <section className="py-16 lg:py-20 px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="space-y-5 text-base sm:text-lg text-slate-700 leading-relaxed"
                    >
                        {page.intro.map((p, i) => (
                            <p key={i}>{p}</p>
                        ))}
                    </motion.div>

                    {page.benefits && (
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.1 }}
                            className="mt-12 rounded-3xl border border-slate-200 bg-[#F5F7FA] p-8 sm:p-10"
                        >
                            <h2 className="font-display text-xl sm:text-2xl font-bold text-[#0F172A]">
                                {page.benefitsTitle}
                            </h2>
                            <CheckList
                                items={page.benefits}
                                testidPrefix="detail-benefit"
                            />
                        </motion.div>
                    )}

                    {page.note && (
                        <motion.div
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.15 }}
                            className="mt-8 rounded-3xl border border-emerald-200 bg-emerald-50/60 p-8 sm:p-10"
                        >
                            <h3 className="font-display text-lg sm:text-xl font-semibold text-[#0F172A]">
                                {page.note.title}
                            </h3>
                            <p className="mt-3 text-slate-700 leading-relaxed">
                                {page.note.text}
                            </p>
                        </motion.div>
                    )}

                    {page.sections &&
                        page.sections.map((s, si) => (
                            <motion.div
                                key={si}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.6 }}
                                className="mt-8 rounded-3xl border border-slate-200 bg-[#F5F7FA] p-8 sm:p-10"
                            >
                                <h2 className="font-display text-xl sm:text-2xl font-bold text-[#0F172A]">
                                    {s.title}
                                </h2>
                                {s.paragraphs && (
                                    <div className="mt-5 space-y-4 text-slate-700 leading-relaxed">
                                        {s.paragraphs.map((p, i) => (
                                            <p key={i}>{p}</p>
                                        ))}
                                    </div>
                                )}
                                {s.list && (
                                    <CheckList
                                        items={s.list}
                                        testidPrefix={`detail-section-${si}`}
                                    />
                                )}
                                {s.closing && (
                                    <p className="mt-5 text-slate-700 leading-relaxed">
                                        {s.closing}
                                    </p>
                                )}
                            </motion.div>
                        ))}

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="relative mt-14 overflow-hidden rounded-3xl bg-[#112338] noise-overlay p-8 sm:p-12 text-center"
                    >
                        <div className="absolute inset-0 blueprint-grid" />
                        <div className="relative">
                            <div className="flex flex-col lg:flex-row lg:items-center gap-8">
                                <div className="flex-1 text-left">
                                    {page.ctaTitle && (
                                        <h3 className="font-mono text-xs uppercase tracking-[0.25em] text-emerald-400">
                                            {page.ctaTitle}
                                        </h3>
                                    )}
                                    <p className="mt-3 font-display text-xl sm:text-2xl font-bold text-white text-balance leading-snug">
                                        {page.cta}
                                    </p>
                                </div>
                                <div className="shrink-0 flex flex-col items-start lg:items-end gap-4">
                                    <button
                                        data-testid="detail-bottom-cta"
                                        onClick={goToForm}
                                        className="group inline-flex items-center gap-2 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0B1A2B] font-bold px-8 py-4 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 whitespace-nowrap"
                                    >
                                        Solicitar estudio gratuito
                                        <ArrowRight
                                            size={17}
                                            className="transition-transform duration-200 group-hover:translate-x-1"
                                        />
                                    </button>
                                    <p className="text-sm text-slate-400">
                                        O llámanos al{" "}
                                        <a
                                            href="tel:+34622344472"
                                            className="text-emerald-400 hover:text-emerald-300 font-semibold"
                                        >
                                            +34 622 344 472
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            <footer className="border-t border-slate-100 py-8 px-4 text-center">
                <p className="text-xs text-slate-400">
                    © 2026 Keypoteca. Tu hipoteca con seguridad y confianza,
                    100% online.
                </p>
            </footer>
        </div>
    );
}
